import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"

import MDBox from "components/MDBox"
import DashboardLayout from "components/LayoutContainers/DashboardLayout"
import Header from "./Header/Header"
import Step1 from "./Step1/Step1"
import Step2 from "./Step2/Step2"
import Step3 from "./Step3/Step3"

import useModels from "hooks/useModels.hook"
import useTestParams from "hooks/useTestParams.hook"
import { styles } from "./MainPage.styles"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import classNames from "classnames"

import TermsAndConditions from "./TermsAndConditions/TermsAndConditions"
import InstractionsScreen from "./InstractionsScreen/InstractionsScreen"
import TestResults from "./TestResults/TestResults"
import SelfReportScreen from "./SelfReportScreen/SelfReportScreen"
import ModalsActions from "./ModalsActions/ModalsActions"

import { useAppSelector, useAppDispatch } from "store/hooks"

import { ResultActionType, UserActions } from "types/resultActions"
import { checkCameraPermission } from "utils/camera.util"
import { TestType } from "types/testType"
import type { ResultStatus } from "types/imageRecognition.d"
import { sentryLogException, sentryLogMessage } from "services/sentryLogger"
import { detectHmo } from "utils/hmoDetection"
import { isSelfReportHmo } from "constants/hmo"
import type {
  StepComponentProps,
  TestTypeStepMap,
  TermsAndConditionsProps,
  InstractionsScreenProps,
  Step1Props,
  Step2Props,
  Step3Props,
  TestResultsProps,
  SelfReportScreenProps,
  ModalsActionsProps,
  StepPropsContext,
  PropsFactory,
} from "./MainPage.types"
import { getModelMetaDataTags } from "services/models"
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents"
import { restoreResult, setNotSureCount } from "store/slices/imageRecognition"
import {
  persistSessionState,
  restoreSessionState,
  clearSessionState,
  isCompletedSessionState,
} from "services/sessionPersistence"

const DEFAULT_TEST_TYPE = TestType.FOB

const MainPage = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [resultAction, setResultAction] = useState<ResultActionType>()
  /** Set once restore effect has run — gates persist so defaults are not written first */
  const restoreAttemptedRef = useRef(false)
  /** Set only when a persisted step > 0 was applied — blocks shouldMoveToCamera override */
  const didRestoreStepRef = useRef(false)
  /** Holds restored payload until first persist — avoids writing step 0 before React state flushes */
  const restoreDataRef = useRef<{
    currentStep: number
    result: ResultStatus | null
    resultAction: ResultActionType | undefined
    notSureCount: number
    testType: string
  } | null>(null)
  const useStyles = makeStyles()((theme) => styles(theme))
  const { classes } = useStyles()
  const result = useAppSelector((state) => state.imageRecognition.result)
  const pendingUpload = useAppSelector((state) => state.imageRecognition.pendingUpload)
  const notSureCount = useAppSelector((state) => state.imageRecognition.notSureCount)
  const dispatch = useAppDispatch()

  const { testType, sessionId, shouldMoveToCamera } = useTestParams()
  useModels()

  const usesSelfReport = isSelfReportHmo(detectHmo())

  /**
   * Restore UI state after WebView process kill.
   * Runs once when sessionId is available; skips if no persisted data.
   * Clears stale session via clearSessionState when testType mismatches URL.
   */
  useEffect(() => {
    if (restoreAttemptedRef.current || !sessionId) {
      return
    }

    const persisted = restoreSessionState(sessionId)
    restoreAttemptedRef.current = true

    if (!persisted) {
      return
    }

    if (persisted.testType !== testType) {
      // URL testType changed — discard stale payload so persist does not overwrite it
      clearSessionState(sessionId)
      return
    }

    if (isCompletedSessionState(persisted)) {
      // Test finished — allow refresh to start a new run from Terms (step 0)
      clearSessionState(sessionId)
      return
    }

    let targetStep = persisted.currentStep

    // pendingUpload is not persisted (base64 images too large) — redo capture only when result is also missing
    if (targetStep >= 5 && usesSelfReport && !pendingUpload && !persisted.result) {
      targetStep = 4
    }

    if (persisted.result) {
      dispatch(restoreResult(persisted.result))
    }

    if (persisted.notSureCount > 0) {
      dispatch(setNotSureCount(persisted.notSureCount))
    }

    if (persisted.resultAction) {
      setResultAction(persisted.resultAction)
    }

    restoreDataRef.current = {
      currentStep: targetStep,
      result: persisted.result,
      resultAction: persisted.resultAction,
      notSureCount: persisted.notSureCount,
      testType: persisted.testType,
    }
    setCurrentStep(targetStep)

    if (targetStep > 0) {
      didRestoreStepRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once; pendingUpload read at mount is sufficient
  }, [sessionId, testType, usesSelfReport, dispatch])

  /**
   * URL param skip-to-camera — only on fresh start.
   * Skipped when restore already ran so persisted step takes precedence.
   */
  useEffect(() => {
    if (didRestoreStepRef.current) {
      return
    }

    ;(async () => {
      try {
        await checkCameraPermission()
        if (shouldMoveToCamera()) {
          setCurrentStep(4)
        }
      } catch (error) {
        sentryLogException({
          error,
          tags: {
            component: 'MainPage',
            category: 'cameraPermission',
            ...getModelMetaDataTags()
          },
          extra: {
            testType,
            timestamp: new Date().toISOString(),
          },
        })
      }
    })()
  }, [shouldMoveToCamera])

  useEffect(() => {
    if (result === "INVALID" || result === "REJECTED") {
      setResultAction("INVALID_TEST")
    }
  }, [result])

  const handleUserAction = useCallback((userAction: UserActions) => {
    const sentryMessageMap: Partial<Record<UserActions, string>> = {
      WRONG_RESULT: "Wrong Result Reported by the user",
      SELF_REPORTING_ISSUE: "Issue reported by the user",
    }

    const message = sentryMessageMap[userAction]
    if (message) {
      try {
        sentryLogMessage({
          message,
          level: "warning",
          tags: {
            component: "Test Completed With Issue",
            category: "test_completed_with_issue",
            event_type: SENTRY_EVENT_TYPES.COMPLETED_TEST_WITH_ISSUE,
            user_action: userAction,
            ...getModelMetaDataTags(),
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        })
      } catch (error) {
        console.error("MainPage: Test Completed With Issue - Sentry logging or flush failed:", error)
      }
    }
    setResultAction('DONE')
  }, [])

  /**
   * Persist state on every step/result change so the next WebView reload can resume.
   * Waits until restore completes (restoreAttemptedRef). On first persist after restore,
   * writes restoreDataRef payload so step 0 is not saved before React state flushes.
   */
  useEffect(() => {
    if (!sessionId || !restoreAttemptedRef.current) {
      return
    }

    if (restoreDataRef.current) {
      persistSessionState(sessionId, restoreDataRef.current)
      restoreDataRef.current = null
      return
    }

    persistSessionState(sessionId, {
      currentStep,
      result,
      resultAction,
      notSureCount,
      testType,
    })
  }, [currentStep, result, resultAction, notSureCount, sessionId, testType])

  const handleUpdateStepLocal = useCallback((step: number) => {
    setCurrentStep((prev) => {
      if (step < 0 || step > 6) {
        return prev
      }
      return step
    })
  }, [])

  // Separate stepMap from result-dependent props to prevent unnecessary remounts
  // Result is only used in step 5's activeInnerStep prop
  const stepMap: TestTypeStepMap = useMemo(() => {
    return {
    [TestType.FOB]: {
      0: {
        Component: TermsAndConditions as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as TermsAndConditionsProps,
        contentHeight: "calc(100dvh - 285px)",
      },
      1: {
        Component: InstractionsScreen as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as InstractionsScreenProps,
      },
      2: {
        Component: Step1 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step1Props,
      },
      3: {
        Component: Step2 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step2Props,
      },
      4: {
        Component: Step3 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step3Props,
      },
      5: {
        Component: (usesSelfReport ? SelfReportScreen : TestResults) as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic result-dependent props (prevents remounting other steps)
        props: usesSelfReport
          ? {
              handleUpdateStep: handleUpdateStepLocal,
              setResultAction: setResultAction,
            } as SelfReportScreenProps
          : ((context: StepPropsContext): TestResultsProps => ({
              handleUpdateStep: handleUpdateStepLocal,
              activeInnerStep: context.result === "POSITIVE" ? 1 : (context.result === "INVALID" ? 3 : 2),
              setResultAction: setResultAction,
            })) as PropsFactory<TestResultsProps>,
        contentHeight: usesSelfReport ? "calc(100dvh - 130px)" : undefined,
      },
      6: {
        Component: ModalsActions as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic resultAction-dependent props
        props: ((context: StepPropsContext): ModalsActionsProps => ({
          handleUserAction: handleUserAction,
          resultAction: context.resultAction,
          handleUpdateStep: handleUpdateStepLocal,
          setResultAction: setResultAction,
        })) as PropsFactory<ModalsActionsProps>,
      },
    },
    [TestType.HPYLORI]: {
      0: {
        Component: TermsAndConditions as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as TermsAndConditionsProps,
        contentHeight: "calc(100dvh - 285px)",
      },
      1: {
        Component: InstractionsScreen as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as InstractionsScreenProps,
      },
      2: {
        Component: Step1 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step1Props,
      },
      3: {
        Component: Step2 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step2Props,
      },
      4: {
        Component: Step3 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step3Props,
      },
      5: {
        Component: (usesSelfReport ? SelfReportScreen : TestResults) as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic result-dependent props (prevents remounting other steps)
        props: usesSelfReport
          ? {
              handleUpdateStep: handleUpdateStepLocal,
              setResultAction: setResultAction,
            } as SelfReportScreenProps
          : ((context: StepPropsContext): TestResultsProps => ({
              handleUpdateStep: handleUpdateStepLocal,
              activeInnerStep: context.result === "POSITIVE" ? 1 : (context.result === "INVALID" ? 3 : 2),
              setResultAction: setResultAction,
            })) as PropsFactory<TestResultsProps>,
        contentHeight: usesSelfReport ? "calc(100dvh - 130px)" : undefined,
      },
      6: {
        Component: ModalsActions as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic resultAction-dependent props
        props: ((context: StepPropsContext): ModalsActionsProps => ({
          handleUserAction: handleUserAction,
          resultAction: context.resultAction,
          handleUpdateStep: handleUpdateStepLocal,
          setResultAction: setResultAction,
        })) as PropsFactory<ModalsActionsProps>,
      },
    },
  }
  // IMPORTANT: Do NOT include 'result' in dependencies - it causes Step3 to remount when result changes
  // Dynamic props are handled via PropsFactory functions instead
  }, [handleUserAction, handleUpdateStepLocal, usesSelfReport, setResultAction])

  const currentStepMap = useMemo(() => {
    return stepMap[testType] || stepMap[DEFAULT_TEST_TYPE]
  }, [stepMap, testType])

  const currentStepConfig = useMemo(() => {
    return currentStepMap[currentStep]
  }, [currentStepMap, currentStep])

  // Use the component directly - don't create inline wrapper which causes remounts
  const StepComponent = currentStepConfig?.Component
  
  // Context for props factory functions
  const propsContext: StepPropsContext = useMemo(() => ({
    result,
    resultAction,
  }), [result, resultAction])

  const stepProps = useMemo(() => {
    const props = currentStepConfig?.props
    
    if (!props) {
      return {} as StepComponentProps
    }
    
    // If props is a factory function, call it with current context
    if (typeof props === 'function') {
      return props(propsContext) as StepComponentProps
    }
    
    // Otherwise, use static props directly
    return props as StepComponentProps
  }, [currentStepConfig, propsContext])

  return (
    <DashboardLayout>
      <Grid className="mainpage-grid" container mt={0} spacing={0}>
        <MDBox pb={1.5}>
          <Header currentStep={currentStep} />
          <div
            className={classNames(
              classes.bodyContainer,
              currentStep === 6 ? classes.step_MODALS : ""
            )}
            style={{ 
              height: currentStepConfig?.contentHeight 
                ? `calc(${currentStepConfig.contentHeight.replace(/^calc\(|\)$/g, '')} - 50px)` 
                : undefined 
            }}
          >
            {StepComponent && (
              <StepComponent 
                key={`${testType}-${currentStep}`} 
                {...stepProps} 
              />
            )}
          </div>
        </MDBox>
      </Grid>
    </DashboardLayout>
  )
}
export default MainPage
